import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, expand, finalize, map, switchMap, takeLast } from 'rxjs/operators';

import { ApiService } from 'src/app/services/api/api.service';

import { canMeasure, LINK_STATUS_INTERVAL_MS, LINK_STATUS_WINDOW_MS } from './link-status-gate';
import { LinkStatusResponse } from './link-status.types';

@Injectable({
  providedIn: 'root',
})
export class LinkStatusGateService {
  canStart = false;
  checking = false;
  lastResponse: LinkStatusResponse | null = null;

  constructor(private readonly api: ApiService) {}

  verify(clientServiceId: number | null): Observable<boolean> {
    this.checking = true;

    if (clientServiceId == null || clientServiceId <= 0) {
      this.lastResponse = null;
      this.canStart = false;
      this.checking = false;
      return of(false);
    }

    const startedAt = Date.now();
    const pull = (): Observable<LinkStatusResponse> => this.api.customer.getLinkStatus(clientServiceId);

    return pull().pipe(
      expand((response) => {
        if (response?.state !== 'pending') {
          return EMPTY;
        }

        const elapsed = Date.now() - startedAt;
        if (elapsed + LINK_STATUS_INTERVAL_MS > LINK_STATUS_WINDOW_MS) {
          return EMPTY;
        }

        return timer(LINK_STATUS_INTERVAL_MS).pipe(switchMap(() => pull()));
      }),
      takeLast(1),
      map((response) => {
        this.lastResponse = response;
        this.canStart = canMeasure(response);
        this.checking = false;
        return this.canStart;
      }),
      catchError(() => {
        this.lastResponse = null;
        this.canStart = false;
        this.checking = false;
        return of(false);
      }),
      finalize(() => {
        this.checking = false;
      }),
    );
  }
}
